<template>
  <div v-if="issueData"
       class="target-view report-image"
       @click="clearHighlight">
    <div class="image-container"
         v-for="data in dataMap"
         :key="data._idx"
         :class="{ active: !isClearHighlight && currentIssue && currentIssue.id === data._idx }"
         :id="`code-id-${data._idx}`">
      <h4 class="data-title">
        <span>{{parseInt(data._idx) + 1}}</span>
        {{data._image}}
      </h4>

      <div class="data-wrap">
        <div v-if="imageUrl[data._image]"
             class="data-image"
             :ref="`dataImage${data._idx}`">
          <img :src="imageUrl[data._image]"
               @error="imageLoaded"
               @load="imageLoaded"
               alt="">
          <div class="data-ocr"
               v-if="ocrResult && ocrResult[data._idx]">
            <div class="sentence"
                 v-for="(ocr, idx) in ocrResult[data._idx]"
                 :key="`ocr-${data._idx}-${idx}`"
                 :style="{
                     width: (ocr.width * imageScale[data._idx]) + 'px',
                     height: (ocr.height * imageScale[data._idx]) + 'px',
                     top: (ocr.top * imageScale[data._idx]) + 'px',
                     left: (ocr.left * imageScale[data._idx]) + 'px'
                   }"
                 :title="ocr.text"
                 :class="{ active: !isClearHighlight && currentIssue && currentIssue.id === data._idx && arrayIdx > -1 && arrayIdx === idx }">
            </div>

            <template v-if="!isClearHighlight && currentIssue && currentIssue.id === data._idx">
              <div v-if="highlightStyle"
                   class="capture-highlight"
                   ref="captureHighlight"
                   :style="highlightStyle"></div>

              <div v-if="maskStyle"
                   class="capture-mask">
                <div :style="maskStyle.top"></div>
                <div :style="maskStyle.left"></div>
                <div :style="maskStyle.right"></div>
                <div :style="maskStyle.bottom"></div>
              </div>

              <div v-if="guideStyle"
                   class="capture-guide">
                <div :style="guideStyle.top"></div>
                <div :style="guideStyle.left"></div>
                <div :style="guideStyle.right"></div>
                <div :style="guideStyle.bottom"></div>
              </div>
            </template>
          </div>
        </div>

        <div class="data-data">
          <table>
            <template v-for="(value, key) in data">
              <tr v-if="key.charAt(0) !== '_'"
                  :key="data._idx + '-' + key">
                <th>
                  {{key}}
                </th>
                <td>
                  {{value}}
                </td>
              </tr>
            </template>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiImageUrl } from '@/lib/http-client'

export default {
  name: 'ReportCapture',
  props: ['report', 'issueData', 'scrollTop', 'dataMap', 'currentIssue', 'refreshKey'],
  data () {
    return {
      arrayIdx: -1,
      isClearHighlight: false,
      ocrResult: null,
      imageScale: {},
      imageSize: {},
      highlightStyle: null,
      maskStyle: null,
      guideStyle: null,
      imageLoadedCount: 0
    }
  },
  computed: {
    imageUrl () {
      const result = {}
      for (let [key, url] of Object.entries(this.report.imageUrl)) {
        result[key] = apiImageUrl(url)
      }
      return result
    }
  },
  watch: {
    currentIssue () {
      this.init()
    },
    refreshKey () {
      setTimeout(() => {
        this.watchDataMap(this.dataMap)
        this.init()
      }, 300)
    },
    scrollTop (value) {
      if (this.$el) {
        this.$el.scrollTop = value
        this.$el.scrollLeft = 0
      }
    },
    dataMap (value) {
      this.watchDataMap(value)
    }
  },
  created () {
    this.watchDataMap(this.dataMap)
  },
  methods: {
    imageLoaded () {
      this.imageLoadedCount++
      if (this.imageLoadedCount >= this.dataMap.length) {
        this.$emit('imageLoaded')
      }
    },
    init () {
      if (this.currentIssue) {
        this.isClearHighlight = false
        this.arrayIdx = this.currentIssue.arrayIdx !== undefined && this.currentIssue.arrayIdx !== null ? this.currentIssue.arrayIdx : -1

        if (this.arrayIdx > -1) {
          const ocrProp = this.ocrResult[this.currentIssue.id][this.arrayIdx]
          let { height, width, top, left } = ocrProp
          height *= this.imageScale[this.currentIssue.id]
          width *= this.imageScale[this.currentIssue.id]
          top *= this.imageScale[this.currentIssue.id]
          left *= this.imageScale[this.currentIssue.id]

          if (left < 0) {
            left = 0
          }

          if (top < 0) {
            top = 0
          }

          if (top + height > this.imageSize[this.currentIssue.id].height) {
            height = this.imageSize[this.currentIssue.id].height - top
          }

          if (left + width > this.imageSize[this.currentIssue.id].width) {
            width = this.imageSize[this.currentIssue.id].width - left
          }

          this.highlightStyle = {
            width: width + 'px',
            height: height + 'px',
            left: left + 'px',
            top: top + 'px'
          }

          this.maskStyle = {
            top: {
              bottom: 'auto',
              height: top + 'px'
            },
            left: {
              top: top + 'px',
              width: left + 'px',
              height: height + 'px',
              bottom: 'auto',
              right: 'auto'
            },
            bottom: {
              top: (top + height) + 'px'
            },
            right: {
              top: top + 'px',
              left: (left + width) + 'px',
              height: height + 'px',
              bottom: 'auto'
            }
          }

          const halfWidth = Math.round(width / 2)
          const halfHeight = Math.round(height / 2)

          this.guideStyle = {
            top: {
              width: '1px',
              left: (left + halfWidth) + 'px',
              height: top + 'px',
              right: 'auto',
              bottom: 'auto',
              marginLeft: '-0.5px'
            },
            left: {
              height: '1px',
              top: (top + halfHeight) + 'px',
              width: left + 'px',
              bottom: 'auto',
              marginTop: '-0.5px'
            },
            bottom: {
              width: '1px',
              left: (left + halfWidth) + 'px',
              top: (top + height) + 'px',
              right: 'auto',
              marginLeft: '-0.5px'
            },
            right: {
              height: '1px',
              top: (top + halfHeight) + 'px',
              left: (left + width) + 'px',
              bottom: 'auto',
              marginTop: '-0.5px'
            }
          }
        }
      } else {
        this.clearHighlight()
      }
    },
    watchDataMap (value) {
      if (value) {
        const getImageData = (data) => {
          return new Promise(resolve => {
            if (data._image && this.imageUrl[data._image]) {
              const img = new Image()
              img.onload = () => {
                this.imageSize[data._idx] = {
                  width: this.$refs[`dataImage${data._idx}`][0].clientWidth,
                  height: this.$refs[`dataImage${data._idx}`][0].clientHeight
                }
                this.imageScale[data._idx] = this.imageSize[data._idx].width / img.width
                resolve(true)
              }
              img.src = this.imageUrl[data._image]
            } else {
              resolve(false)
            }
          })
        }

        let promises = []
        if (this.dataMap) {
          for (const data of this.dataMap) {
            promises.push(getImageData(data))
          }
        }

        Promise.all(promises)
          .finally(() => {
            const resultData = this.report.ocrDataObject
            const ocrResult = {}
            for (const [key, value] of Object.entries(resultData)) {
              if (!ocrResult[key]) {
                ocrResult[key] = []
              }
              for (const v of Object.values(value)) {
                ocrResult[key].push(v)
              }
              ocrResult[key].sort((a, b) => parseFloat(b.conf) - parseFloat(a.conf))
            }
            this.ocrResult = ocrResult
          })
      }
    },
    clearHighlight () {
      this.isClearHighlight = true
      this.arrayIdx = -1
    }
  }
}
</script>

<style lang="scss" scoped>
.target-view {
  width: 100%;
  height: 100%;
  position: relative;
  overflow-x: hidden;
  overflow-y: auto;
  background: transparent;

  .image-container {
    text-align: left;
    position: relative;
    margin-bottom: 20px;
    margin-right: 5px;

    &:last-child {
      margin-bottom: 0;
    }

    .data-title {
      font-size: 14px;
      font-weight: 500;
      line-height: 1.29;
      color: #444444;
      margin-top: 5px;
      margin-left: 3px;
      margin-bottom: 7px;

      span {
        display: inline-block;
        width: 26px;
        height: 26px;
        margin: -2px 5px 0 0;
        border-radius: 4px;
        border: solid 1px rgba(0, 0, 0, 0.12);
        background-color: #ffffff;
        vertical-align: middle;
        text-align: center;
        line-height: 26px;
        font-weight: 600;
        font-size: 16px;
        color: #222222;
      }
    }

    .data-wrap {
      padding: 10px;
      border-radius: 10px;
      background-color: #151515;
    }

    .data-image {
      position: relative;

      img {
        width: 100%;
        margin-bottom: -7px;
        border: 1px solid rgba(255, 255, 255, 0.3);;
      }

      .data-ocr {
        .sentence {
          position: absolute;
          background: yellow;
          opacity: 0.3;
          border: 1px solid orange;
          transform: translate(-1px, -1px);

          &:hover {
            opacity: 0;
          }

          &.active {
            opacity: 0;
          }
        }

        .capture-highlight {
          position: absolute;
          outline: 1px solid #ffe30d;
          z-index: 1;
        }

        .capture-mask > div,
        .capture-guide > div {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5)
        }

        .capture-guide > div {
          background-color: #ffe30d;
        }
      }
    }

    .data-data {
      line-height: 1.29;
      letter-spacing: normal;
      color: #d7d7d7;
      padding-top: 6px;
      padding-bottom: 15px;

      table {
        th {
          font-size: 13px;
          padding: 13px 5px 11px 6px;
          width: 100px;
          font-weight: 400;
          vertical-align: top;
        }

        td {
          font-size: 14px;
          padding: 11px 10px 11px 6px;
          border-top: 1px solid #444;
          font-weight: 300;
          vertical-align: top;
        }

        tr:first-child {
          td {
            border-top: none;
          }
        }
      }
    }
  }
}

</style>



// WEBPACK FOOTER //
// src/components/valid-member/report/ReportImage.vue